import { hours, num } from '../../lib/format'
import { tulipAbstaende } from '../../lib/tulip'
import type { DiaryEntry, PhotoMeta, Stage, Tulip } from '../../lib/types'
import { ATTRIBUTION } from '../map/basemap'
import { differenzGenau, etappenVergleich, kilometerGenau } from '../map/etappenVergleich'
import { germanDate, germanWeekday } from './metrics'
import { NotizLinien, PageFrame } from './PageFrame'
import { PrintPhoto } from './PrintPhoto'
import { StageSketch } from './StageSketch'
import {
  landZuLang,
  ROADBOOK_TULPEN,
  type DruckBeitrag,
  type StagePagePlan,
  type StagePlan,
} from './stagePlan'
import { TulipRows } from './TulipRows'

interface Props {
  stage: Stage
  entry: DiaryEntry
  photos: readonly PhotoMeta[]
  plan: StagePlan
  /**
   * Der Reisetag dieser Etappe, zweistellig - die Zahl links im Kopf.
   *
   * Kommt von aussen, weil eine einzelne Etappe ihn nicht kennen kann: er
   * hängt daran, wie viele Etappen vor ihr ausgelassen sind. Gerechnet wird er
   * in lib/calendar.ts, gereicht von PrintBook.
   */
  tag: string
  /** Die wievielte Seite dieser Etappe, von 1 an. */
  part: number
  page: number
  fuss: string
  /** Der gerenderte Kartenausschnitt. Fehlt er, kommt die Skizze. */
  mapUrl?: string
  photoUrls: Record<string, string>
  /**
   * Je Tulpe dieser Etappe: ob ihre Form im Buch hier zum ersten Mal auftaucht.
   *
   * Gerechnet über das ganze Buch und deshalb nicht hier - eine Etappe weiss
   * nicht, was auf den Seiten davor stand. Siehe erstesVorkommen in
   * lib/tulip.ts und den Aufruf in PrintBook.tsx.
   */
  erstmals?: readonly boolean[]
}

/**
 * Eine Etappe auf einer Seite: Kopfzone, Kartenausschnitt, Planung, Tagebuch,
 * Fotos.
 *
 * Der Aufbau folgt dem Blick: oben steht in einer festen Zone, worum es geht -
 * Reisetag links, Ziel in der Mitte, Datum rechts, darunter die
 * Kennzahlenleiste. Die Zahl links und das Datum rechts reden über denselben
 * Tag; deshalb ist es der Reisetag und nicht die Stelle in der Etappenliste.
 *
 * Die Dreiteilung ist von den Zeilen gedruckter
 * Rallye-Roadbooks geborgt: Distanzen links, Skizze in der Mitte, Notiz rechts.
 * Übernommen ist davon die Aufteilung der Kopfleiste, nicht das Seitenformat -
 * ein Roadbook ist eine Rolle mit lauter gleichen Zeilen, ein Reisetagebuch
 * lebt gerade davon, dass jede Seite anders aussieht.
 *
 * Wie viel Platz Text und Fotos bekommen, entscheidet die Vorlage aus
 * stagePlan.ts. Sie steht als Klasse an der Seite, und print.css lässt je nach
 * Vorlage einen anderen Bereich wachsen: bei bildstark die Fotos, bei textstark
 * der Text, dazwischen die Notizlinien.
 */
export function StagePage(props: Props) {
  const { stage, plan, part } = props

  if (part > 1) return <ContinuationPage {...props} />

  const datum = props.entry.date

  /*
   * Was in den Planungsspalten steht, hat stagePlan.ts entschieden und nicht
   * diese Komponente. Der Grund ist derselbe wie beim Text: die Höhe dieser
   * Spalten geht dem Tagebuch von unten ab, und wer sie hier noch einmal
   * eigenständig kürzen würde, setzte etwas anderes, als die Seitenplanung
   * gerechnet hat - genau die Lücke, durch die vorher ein einziges langes
   * Highlight den halben Tagebucheintrag hinter overflow:hidden geschoben hat.
   */
  const wochentag = germanWeekday(datum)
  const planung = plan.planung
  const vergleich = etappenVergleich(stage, props.entry)
  const land = stage.land || '—'

  return (
    <PageFrame kind="etappe" page={props.page} fuss={props.fuss}>
      <div className={`dr-etappe dr-etappe--${plan.template}`}>
        <header className="dr-kopf">
          <div className="dr-kopf-zeile">
            <span className="dr-tag">{props.tag}</span>
            <h2 className="dr-ziel">
              {stage.from} <span className="dr-pfeil">→</span> {stage.to}
            </h2>
            <span className="dr-kopf-datum">
              {datum ? `${wochentag}, ${germanDate(datum, true)}` : stage.land}
            </span>
          </div>

          <dl className={vergleich ? 'dr-leiste dr-leiste--vergleich' : 'dr-leiste'}>
            {vergleich ? (
              <>
                <div>
                  <dt>GPX gefahren</dt>
                  <dd>{kilometerGenau(vergleich.gefahrenKm)} km</dd>
                </div>
                <div>
                  <dt>Route geplant</dt>
                  <dd>{kilometerGenau(vergleich.geplantKm)} km</dd>
                </div>
                <div>
                  <dt>Differenz</dt>
                  <dd>{differenzGenau(vergleich.differenzKm)} km</dd>
                </div>
              </>
            ) : (
              <div>
                <dt>Kilometer</dt>
                <dd>{num(stage.km)}</dd>
              </div>
            )}
            <div>
              <dt>Stunden</dt>
              <dd>{hours(stage.h)}</dd>
            </div>
            <div>
              <dt>Land</dt>
              {/*
               * Ausgeschrieben oder als Kürzel - beides steht im Feld, und
               * beides soll lesbar sein. Passt der Eintrag nicht auf eine
               * Zeile, wird er kleiner gesetzt statt abgeschnitten; siehe
               * landZuLang und .dr-land-lang.
               */}
              <dd className={landZuLang(land) ? 'dr-land-lang' : undefined}>{land}</dd>
            </div>
            <div>
              <dt>Übernachtung</dt>
              <dd>{stage.camping[0]?.name ?? '—'}</dd>
            </div>
          </dl>
        </header>

        <StageMap {...props} />

        <div className="dr-spalten">
          <section className="dr-block">
            <h3>Unterwegs</h3>
            {planung.highlights.length > 0 ? (
              <ul>
                {planung.highlights.map((eintrag, i) => (
                  <li key={i}>{eintrag}</li>
                ))}
                {planung.highlightsRest > 0 && (
                  <li className="dr-mehr">und {planung.highlightsRest} weitere</li>
                )}
              </ul>
            ) : (
              <p className="dr-leer">nichts notiert</p>
            )}

            {planung.night.length > 0 && (
              <>
                <h3 className="dr-block-zweit">Abends</h3>
                <ul>
                  {planung.night.map((eintrag, i) => (
                    <li key={i}>{eintrag}</li>
                  ))}
                  {planung.nightRest > 0 && (
                    <li className="dr-mehr">und {planung.nightRest} weitere</li>
                  )}
                </ul>
              </>
            )}
          </section>

          <section className="dr-block">
            <h3>Schlafen</h3>
            {planung.camping.length > 0 ? (
              <ul>
                {planung.camping.map((platz, i) => (
                  <li key={i}>
                    <b>{platz.name}</b>
                    {platz.price && <span className="dr-preis"> {platz.price}</span>}
                    {platz.note && <small>{platz.note}</small>}
                  </li>
                ))}
                {planung.campingRest > 0 && (
                  <li className="dr-mehr">und {planung.campingRest} weitere</li>
                )}
              </ul>
            ) : (
              <p className="dr-leer">offen</p>
            )}

            {planung.tip && <p className="dr-hinweis">{planung.tip}</p>}
            {/*
              Ein Tipp, der ganz weggefallen ist, hinterlässt sonst nichts -
              anders als eine gekürzte Liste, die ihre Zeile "und N weitere"
              behält. Also sagt er es selbst.
            */}
            {planung.tipWeg && <p className="dr-mehr">Tipp: siehe App</p>}
          </section>
        </div>

        {/*
          Der Tulpenstreifen steht zwischen Planung und Tagebuch: er gehört zu
          dem, was der Tag vorhat, und nicht zu dem, was er wurde. Wie viele
          Zeilen hier stehen dürfen, entscheidet stagePlan.ts - der Streifen
          nimmt dem Text Platz weg, und diese Rechnung ist dort schon gemacht.
        */}
        <StageTulips stage={stage} plan={plan.first} erstmals={props.erstmals} />

        <StageDiary entry={props.entry} plan={plan.first} />
        <PhotoRow plan={plan.first} photos={props.photos} urls={props.photoUrls} stage={stage} />
      </div>
    </PageFrame>
  )
}

/** Die Fortsetzung einer Etappe: laufende Kopfzeile, Text, Bilder oder Tulpen. */
function ContinuationPage(props: Props) {
  const { stage, plan, part } = props
  const seite = plan.continuations[part - 2]
  if (!seite) return null

  const roadbook = seite.tulips.length > 0
  const zeilen = tulpenZeilen(stage, seite.tulips, props.erstmals)

  return (
    <PageFrame
      kind={roadbook ? 'roadbook' : 'etappe-fort'}
      page={props.page}
      fuss={props.fuss}
      laufend={`Tag ${props.tag} · ${stage.from} → ${stage.to}`}
      marke={roadbook ? 'Roadbook' : undefined}
    >
      {/*
        Eine Fortsetzung trägt entweder Tulpen, Text oder Bilder, nie zweierlei
        - so entscheidet es stagePlan.ts. Bleibt unter dem Text Platz, wird er zu
        Linien; unter Bildern nicht, dort füllt das Raster die Seite. Auf einer
        Roadbookseite werden die übrigen Zeilen zu leeren Tulpen.
      */}
      <div className="dr-etappe dr-etappe--fortsetzung">
        {roadbook ? (
          <TulipRows
            tulips={zeilen.map((z) => z.tulip)}
            benennen={zeilen.map((z) => z.erstmals)}
            abstaende={zeilen.map((z) => z.abstand)}
            blanks={ROADBOOK_TULPEN - seite.tulips.length}
          />
        ) : (
          <>
            <Beitraege beitraege={seite.beitraege} />
            <RestHinweis zeichen={seite.ungedruckt} />
            <PhotoRow plan={seite} photos={props.photos} urls={props.photoUrls} stage={stage} />
            {seite.photos.length === 0 && <NotizLinien anzahl={42} />}
          </>
        )}
      </div>
    </PageFrame>
  )
}

/**
 * Die Tulpen einer Seite, samt der Frage, ob ihr Name danebensteht, und samt
 * der Entfernung bis zur nächsten Anweisung.
 *
 * Alles zusammen und nicht drei getrennte Listen: die Stellen, die es nicht
 * gibt, fallen hier heraus, und ohne diese gemeinsame Auswahl stünden Name und
 * Abstand ab der ersten fehlenden Stelle an der falschen Tulpe.
 *
 * Der Abstand wird über die ganze Etappe gerechnet und erst danach auf die
 * Seite geschnitten. Anders ginge es nicht: die letzte Zeile einer
 * Roadbookseite meint die erste der nächsten Seite, und eine Rechnung über den
 * Ausschnitt behauptete an jedem Seitenwechsel ein Etappenende.
 */
function tulpenZeilen(stage: Stage, stellen: readonly number[], erstmals?: readonly boolean[]) {
  const alle = stage.tulips ?? []
  /*
   * Die berechnete Strecke und nicht stage.km, wo es sie gibt: die
   * Kilometerstände der Tulpen stammen aus derselben Antwort des Routendienstes
   * (kilometer() in lib/tulipSuggest.ts), und eine Differenz zwischen zwei
   * Skalen ist keine Entfernung. Ohne berechnete Strecke bleibt die geplante
   * Zahl - dieselbe, die oben in der Kennzahlenleiste steht.
   */
  const abstaende = tulipAbstaende(alle, stage.route?.distanceKm ?? stage.km)

  return stellen
    .map((i) => ({
      tulip: alle[i],
      erstmals: erstmals?.[i] === true,
      abstand: abstaende[i] ?? '',
    }))
    .filter(
      (zeile): zeile is { tulip: Tulip; erstmals: boolean; abstand: string } =>
        zeile.tulip !== undefined,
    )
}

/** Der Tulpenstreifen der Etappenseite: die ersten Zeilen des Roadbooks. */
function StageTulips({
  stage,
  plan,
  erstmals,
}: {
  stage: Stage
  plan: StagePagePlan
  erstmals?: readonly boolean[]
}) {
  if (plan.tulips.length === 0) return null

  const gezeigt = tulpenZeilen(stage, plan.tulips, erstmals)
  if (gezeigt.length === 0) return null

  const rest = (stage.tulips ?? []).length - plan.tulips.length

  return (
    <section className="dr-tulpenblock">
      <h3>
        Roadbook
        {/*
          Der Hinweis auf den Rest ist keine Höflichkeit, sondern das, was eine
          abgeschnittene Liste sonst verschweigt: hinter dieser Seite stehen
          noch Zeilen, und wer sie nicht sucht, fährt an ihnen vorbei.
        */}
        {rest > 0 && <span className="dr-tulpen-rest">und {rest} weitere, nächste Seite</span>}
      </h3>
      <TulipRows
        tulips={gezeigt.map((z) => z.tulip)}
        benennen={gezeigt.map((z) => z.erstmals)}
        abstaende={gezeigt.map((z) => z.abstand)}
      />
    </section>
  )
}

/** Der Kartenausschnitt, mit der Skizze als Rückfallebene. */
function StageMap({ stage, entry, mapUrl }: Props) {
  const vergleich = etappenVergleich(stage, entry)

  return (
    <figure className="dr-karte">
      {mapUrl ? (
        <img
          src={mapUrl}
          alt={
            vergleich
              ? `Karte mit geplanter und aufgezeichneter Strecke von ${stage.from} nach ${stage.to}`
              : `Karte der Etappe ${stage.from} nach ${stage.to}`
          }
          loading="eager"
        />
      ) : (
        <StageSketch stage={stage} entry={entry} driven={entry.done} />
      )}
      {/*
        Die Namensnennung steht als Text daneben und nicht im Bild: das
        Bedienelement von MapLibre ist ein DOM-Element und landet im Export
        gerade nicht. Die ODbL verlangt sie trotzdem, und auf Papier lässt sie
        sich nicht nachträglich aufklappen.
      */}
      <figcaption>
        {mapUrl ? ATTRIBUTION : 'Skizze, ohne Kartendaten'}
        {vergleich
          ? ' · Planung und aufgezeichnete Fahrt'
          : entry.track && ' · aufgezeichnete Fahrt'}
      </figcaption>
    </figure>
  )
}

/** Die Tagebuchzeile und der Text des Tages. */
function StageDiary({ entry, plan }: { entry: DiaryEntry; plan: StagePagePlan }) {
  const werte: string[] = []
  if (entry.date) werte.push(germanDate(entry.date))
  if (entry.odo) werte.push(`Tacho ${entry.odo}`)
  if (entry.realKm) werte.push(`${entry.realKm} km gefahren`)
  if (entry.weather) werte.push(entry.weather)

  return (
    <section className="dr-tagebuch">
      <div className="dr-tagebuch-kopf">
        <h3>Tagebuch</h3>
        <span className="dr-tagebuch-werte">{werte.join(' · ')}</span>
      </div>

      <Beitraege beitraege={plan.beitraege} />
      <RestHinweis zeichen={plan.ungedruckt} />

      {/*
        Was der Text übrig lässt, wird zum Platz zum Schreiben. Der Kasten
        nimmt sich genau den Rest und verschwindet, wenn keiner da ist - eine
        Etappe ohne Eintrag ergibt so eine Seite zum Vollschreiben statt einer
        leeren unteren Hälfte. Das Buch soll auch als leeres Heft mitfahren
        können.
      */}
      <NotizLinien anzahl={20} />
    </section>
  )
}

/**
 * Die Beiträge einer Seite, als ein Text gesetzt.
 *
 * Ein Beitrag ist ein Absatz, mehr nicht. Kein Kasten, kein Hintergrund, keine
 * Einrückung, keine Trennlinie: der Wechsel wird durch Typografie markiert und
 * nicht durch Möblierung. Ein Chatverlauf auf Papier sähe aus wie ein
 * abfotografierter Messenger; im Buch soll der Tag als ein Text dastehen, an
 * dem zwei Leute geschrieben haben.
 *
 * Der Verfassername steht als Vorspann in der Zeile, wie die Zeile "VON ... —"
 * in einer Zeitschrift, und der Text läuft im selben Satzspiegel dahinter
 * weiter. Ob überhaupt einer gesetzt wird, hat druckBeitraege entschieden: bei
 * einem einzigen Verfasser steht keiner, denn dann ist der Beitrag einfach der
 * Text des Tages.
 *
 * Uhrzeiten stehen hier nicht. Dass jemand um 21:03 getippt hat, ist keine
 * Auskunft über die Reise, sondern über die Bedienung der App - das Datum des
 * Tages steht ohnehin schon in der Kopfzeile und in der Tagebuchzeile. Auf
 * Papier lässt sich so etwas nicht wegklappen; in der App steht es weiterhin an
 * jedem Beitrag.
 *
 * Der Abstand zwischen zwei Absätzen kostet Platz, und stagePlan.ts rechnet
 * damit (BEITRAG_ABSTAND_ZEILEN). Wer ihn in print.css ändert, ändert auch
 * dort.
 */
function Beitraege({ beitraege }: { beitraege: readonly DruckBeitrag[] }) {
  return (
    <>
      {beitraege.map((beitrag, i) => (
        <p className="dr-text dr-beitrag" key={i}>
          {beitrag.vorspann && (
            <span className="dr-vorspann">
              {beitrag.vorspann} <span className="dr-vorspann-strich">—</span>
            </span>
          )}
          {beitrag.vorspann && ' '}
          {beitrag.text}
        </p>
      ))}
    </>
  )
}

/** Die Fotos einer Seite, in den Plätzen ihrer Vorlage. */
function PhotoRow({
  plan,
  photos,
  urls,
  stage,
}: {
  plan: StagePagePlan
  photos: readonly PhotoMeta[]
  urls: Record<string, string>
  stage: Stage
}) {
  if (plan.photos.length === 0) return null

  return (
    <div className="dr-fotos">
      {plan.photos.map((platz) => {
        const foto = photos[platz.index]
        if (!foto) return null
        return (
          <PrintPhoto
            key={foto.id}
            url={urls[foto.id]}
            slot={platz.slot}
            alt={`Foto von der Etappe nach ${stage.to}`}
          />
        )
      })}
    </div>
  )
}

/**
 * Der Satz, der aus einem stummen Abschnitt einen sichtbaren macht.
 *
 * Ein Tagebucheintrag jenseits von neun Seiten ist ein Ausnahmefall, und die
 * Reissleine in stagePlan.ts ist die richtige Antwort darauf. Falsch war nur,
 * dass sie nichts gesagt hat: der Rest landete in einem letzten Stück, von dem
 * eine Seite gedruckt wurde und der Rest hinter overflow:hidden verschwand. Wer
 * im Auto sitzt und das Buch in der Hand hat, kann nicht nachsehen, was er nicht
 * sieht - also steht es hier.
 */
function RestHinweis({ zeichen }: { zeichen?: number }) {
  if (!zeichen || zeichen <= 0) return null

  return (
    <p className="dr-rest">
      Hier bricht der Eintrag ab: {zeichen} Zeichen haben im Buch keinen Platz mehr. Vollständig
      steht er in der App.
    </p>
  )
}
